import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Rocket,
  ShieldCheck,
  Zap,
  Users,
  Lock,
  RefreshCw,
  Server,
  Gauge,
  Globe,
  Radar,
  Headphones,
  MessageSquare,
  Compass,
  Ear,
  Layers,
  GitBranch,
  ShieldAlert,
  Eye,
} from 'lucide-react';

import { breakpoints } from '@/layouts/breakpoints';

const StyledComp = styled.div`
  padding: 160px 40px;
  padding-top: 240px ;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${breakpoints.small}) {
    padding: 40px 24px;
    padding-top: 60px ;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .header {
    margin-bottom: 40px;
  }

  .title {
    font-size: 48px;
    line-height: 1.2;
    color: var(--black);
    margin: 0 0 8px 0;
    max-width: 640px;
  }

  .subtitle {
    font-size: 15px;
    line-height: 1.5;
    color: var(--grey);
    margin: 0;
    max-width: 560px;
  }

  .tabs {
    display: flex;
    gap: 24px;
    margin: 32px 0 0 0;
    border-bottom: 1px solid rgba(var(--blackr), 0.08);
  }

  .tab {
    background: none;
    border: none;
    padding: 0 0 12px 0;
    font-size: 14px;
    font-weight: 500;
    color: var(--grey);
    cursor: pointer;
    position: relative;
    transition: color 0.25s ease;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 2px;
      background: var(--black);
      opacity: 0;
      transform: scaleX(0.85);
      transition: opacity 0.25s ease, transform 0.25s ease;
    }

    &.active {
      color: var(--black);
    }

    &.active::after {
      opacity: 1;
      transform: scaleX(1);
    }
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    background: rgba(var(--blackr), 0.08);
    margin-top: 32px;
    animation: fadeIn 0.4s ease;

    @media (max-width: 900px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 560px) {
      grid-template-columns: 1fr;
    }
  }

  .item {
    background: #fff;
    padding: 28px 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    animation: fadeIn 0.45s ease backwards;
  }

  .icon-wrap {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .item-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--black);
    margin: 0;
  }

  .item-desc {
    font-size: 13px;
    line-height: 1.5;
    color: var(--grey);
    margin: 0;
  }
`;

const tabData = {
  Platform: {
    items: [
      { icon: Rocket, title: 'Built for financial-grade accuracy', description: 'Models and data pipelines held to the standards institutional workflows require.' },
      { icon: RefreshCw, title: 'Continuous product iteration', description: 'We ship improvements weekly based on how the platform is actually used.' },
      { icon: Layers, title: 'API-first architecture', description: 'Every feature is built to be composed into existing systems, not bolted on.' },
      { icon: GitBranch, title: 'Engineered for scale', description: 'Infrastructure designed to handle growing data volume without added complexity.' },
      { icon: Compass, title: 'Designed with practitioners', description: 'Analysts and traders shape the roadmap alongside our product team.' },
      { icon: Server, title: 'Cutting-edge modeling tools', description: 'We push automation further so teams spend less time on manual analysis.' },
    ],
  },
  Security: {
    items: [
      { icon: Lock, title: 'Bank-grade encryption standards', description: 'Data is encrypted at rest and in transit across every environment.' },
      { icon: ShieldCheck, title: 'Continuous security audits', description: 'Independent reviews run on a recurring schedule, not a one-time checkbox.' },
      { icon: ShieldAlert, title: 'Strict data access controls', description: 'Access is scoped and logged, so only the right people see the right data.' },
      { icon: Eye, title: 'Privacy by design', description: 'Data handling decisions start with privacy, not get retrofitted onto it.' },
      { icon: Radar, title: 'Zero-trust internal architecture', description: 'Every internal request is verified, regardless of where it originates.' },
      { icon: ShieldCheck, title: 'Regular penetration testing', description: 'Third-party testers probe our systems so we find gaps before anyone else does.' },
    ],
  },
  Speed: {
    items: [
      { icon: Zap, title: 'Instant data retrieval', description: 'Decades of market and financial history, retrievable nearly instantly.' },
      { icon: Gauge, title: 'Deep historical coverage', description: 'Daily and hourly data across decades for long-horizon research.' },
      { icon: Server, title: 'Always-on infrastructure', description: 'Redundant systems keep the platform available when markets are moving.' },
      { icon: RefreshCw, title: 'Rapid deployment cycles', description: 'New capabilities reach users in days, not quarters.' },
      { icon: Zap, title: 'Instant query response', description: 'Complex financial queries return answers without the wait.' },
      { icon: Globe, title: 'Global data network', description: 'Distributed infrastructure keeps coverage consistent wherever you work.' },
    ],
  },
  People: {
    items: [
      { icon: Users, title: 'Built around user workflows', description: 'We design for how strategies actually get built, not a generic use case.' },
      { icon: MessageSquare, title: 'Direct customer feedback loops', description: 'User input reaches the product team without layers in between.' },
      { icon: Headphones, title: 'Dedicated support teams', description: 'Real people who understand financial workflows, not a ticket queue.' },
      { icon: Compass, title: 'User-first product roadmap', description: 'What we build next is driven by what users need to succeed.' },
      { icon: Ear, title: 'Continuous listening & iteration', description: 'We treat every conversation with users as an input to the next release.' },
      { icon: Users, title: 'Hands-on onboarding support', description: 'Teams get set up with guidance tailored to their strategy, not a generic guide.' },
    ],
  },
};

const tabs = Object.keys(tabData);

const Comp = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);

  return (
    <StyledComp>
      <div className="header">
        <h2 className="title serif">What drives how we build AxionQuant</h2>
        <p className="subtitle">
          The principles behind our financial data platform, our security posture, and how we work with the people who rely on us.
        </p>
      </div>

      <div className="tabs">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`tab ${tab === activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="grid" key={activeTab}>
        {tabData[activeTab].items.map(({ icon: Icon, title, description }, index) => (
          <div className="item" key={title} style={{ animationDelay: `${index * 0.04}s` }}>
            <div className="icon-wrap">
              <Icon size={20} color="var(--grey)" strokeWidth={1.75} />
            </div>
            <p className="item-title">{title}</p>
            <p className="item-desc">{description}</p>
          </div>
        ))}
      </div>
    </StyledComp>
  );
};

export default Comp;
